import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { createDashboardPresentation, createPartnerDashboardPresentation } from "../../api/client";
import { formatDashboardPeriodLabel } from "../../utils/dashboardDeepLink";

const EXPIRY_PRESETS = [
  { hours: 1, seconds: 3600 },
  { hours: 6, seconds: 6 * 3600 },
  { hours: 24, seconds: 24 * 3600 },
  { hours: 48, seconds: 48 * 3600 },
];

export default function DashboardPresentationShareDialog({
  open,
  onClose,
  tabKey,
  tabLabel,
  start,
  end,
  surveyId,
  surveyTitle,
  variant = "workspace",
  dominioIds = [],
  partnerSurveyKey = null,
}) {
  const { t, i18n } = useTranslation("settings");
  const [title, setTitle] = React.useState("");
  const [expiresInSec, setExpiresInSec] = React.useState(24 * 3600);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [result, setResult] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setTitle(tabLabel || "");
    setExpiresInSec(24 * 3600);
    setError("");
    setResult(null);
    setCopied(false);
  }, [open, tabLabel]);

  const periodLabel = formatDashboardPeriodLabel(start, end, i18n.language);

  const handleCreate = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = {
        tab: tabKey,
        title: title.trim() || tabLabel,
        start,
        end,
        expiresInSec,
      };
      if (tabKey === "surveys") {
        if (variant === "partner" && partnerSurveyKey) {
          payload.partnerSurveyKey = partnerSurveyKey;
        } else if (surveyId) {
          payload.surveyId = surveyId;
          payload.surveyTitle = surveyTitle || null;
        }
      }
      const data =
        variant === "partner"
          ? await createPartnerDashboardPresentation({
              ...payload,
              ...(Array.isArray(dominioIds) && dominioIds.length
                ? { dominio_ids: dominioIds.map((x) => String(x)).join(",") }
                : {}),
            })
          : await createDashboardPresentation(payload);
      setResult(data);
    } catch (e) {
      const status = e?.response?.status ?? e?.status;
      setError(
        status === 409
          ? t("dashboardPresentation.cacheMiss", {
              defaultValue:
                "O relatório ainda não está em cache. Aguarde o carregamento completo na tela e tente novamente.",
            })
          : e?.response?.data?.message ||
              e?.message ||
              t("dashboardPresentation.createFailed", { defaultValue: "Não foi possível criar o link." })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.url) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError(
        t("dashboardPresentation.copyFailed", { defaultValue: "Não foi possível copiar o link." })
      );
    }
  };

  const surveysBlocked =
    tabKey === "surveys" && !(variant === "partner" ? partnerSurveyKey : surveyId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("dashboardPresentation.title", { defaultValue: "Compartilhar apresentação" })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t("dashboardPresentation.description", {
              defaultValue:
                "Gere um link público (sem login) para enviar a líderes e stakeholders. O painel fica congelado no período selecionado até expirar.",
            })}
          </Typography>

          {periodLabel ? (
            <Alert severity="info" variant="outlined">
              {t("dashboardPresentation.period", { defaultValue: "Período" })}: {periodLabel}
              {tabLabel ? ` · ${tabLabel}` : ""}
            </Alert>
          ) : null}

          {surveysBlocked ? (
            <Alert severity="warning">
              {t("dashboardPresentation.selectSurvey", {
                defaultValue: "Selecione uma pesquisa na aba antes de compartilhar.",
              })}
            </Alert>
          ) : null}

          {!result ? (
            <>
              <TextField
                label={t("dashboardPresentation.linkTitle", { defaultValue: "Título da apresentação" })}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                disabled={loading}
              />
              <FormControl fullWidth disabled={loading}>
                <InputLabel id="pres-expiry-label">
                  {t("dashboardPresentation.expiry", { defaultValue: "Validade do link" })}
                </InputLabel>
                <Select
                  labelId="pres-expiry-label"
                  label={t("dashboardPresentation.expiry", { defaultValue: "Validade do link" })}
                  value={expiresInSec}
                  onChange={(e) => setExpiresInSec(Number(e.target.value))}
                >
                  {EXPIRY_PRESETS.map((p) => (
                    <MenuItem key={p.seconds} value={p.seconds}>
                      {t("dashboardPresentation.expiryHours", {
                        defaultValue: "{{count}} horas",
                        count: p.hours,
                      })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t("dashboardPresentation.linkReady", { defaultValue: "Link pronto para enviar" })}
              </Typography>
              <TextField
                value={result.url}
                fullWidth
                multiline
                minRows={2}
                InputProps={{ readOnly: true }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                {t("dashboardPresentation.validUntil", {
                  defaultValue: "Válido até {{date}}",
                  date: result.expiresAt
                    ? new Date(result.expiresAt).toLocaleString(i18n.language)
                    : "—",
                })}
              </Typography>
            </Box>
          )}

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {result
            ? t("dashboardPresentation.close", { defaultValue: "Fechar" })
            : t("dashboardPresentation.cancel", { defaultValue: "Cancelar" })}
        </Button>
        {!result ? (
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ShareIcon />}
            onClick={handleCreate}
            disabled={loading || surveysBlocked}
          >
            {loading
              ? t("dashboardPresentation.generating", { defaultValue: "Gerando link…" })
              : t("dashboardPresentation.create", { defaultValue: "Gerar link" })}
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={handleCopy}
          >
            {copied
              ? t("dashboardPresentation.copied", { defaultValue: "Copiado!" })
              : t("dashboardPresentation.copy", { defaultValue: "Copiar link" })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
